import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { guardedNavigate } from '../lib/navGuard';
import {
  Scale,
  Plus,
  FolderOpen,
  Search,
  ChevronDown,
  Settings,
  ClipboardList,
  Upload,
  Landmark,
  FileText,
  MessageCircle,
  Shield,
  FlaskConical,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import { Button, IconButton, Input } from './ui';
import { useCases } from '../hooks/useCases';
import type { Case } from '../types';

const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && !import.meta.env.DEV;

interface CaseSection {
  id: string;
  label: string;
  icon: LucideIcon;
  defaultPath: string;
}

const CASE_SECTIONS: CaseSection[] = [
  { id: 'discovery', label: 'Discovery', icon: Upload, defaultPath: 'discovery/upload' },
  { id: 'investigation', label: 'Investigation', icon: Search, defaultPath: 'investigation/transactions' },
  { id: 'estate', label: 'Estate', icon: Landmark, defaultPath: 'estate/split' },
  { id: 'drafts', label: 'Drafts', icon: FileText, defaultPath: 'drafts/letters' },
  { id: 'assistant', label: 'Assistant', icon: MessageCircle, defaultPath: 'assistant' },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: cases = [] } = useCases();
  const { data: whoami } = useQuery({
    queryKey: ['audit-whoami'],
    queryFn: async () => (await api.get('/audit-log/whoami')).data as { is_admin: boolean },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const isAdmin = !!whoami?.is_admin;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const slugMatch = location.pathname.match(/^\/cases\/([^/]+)/);
  const slug = slugMatch && slugMatch[1] !== 'new' && slugMatch[1] !== 'archived'
    ? slugMatch[1]
    : null;
  const selectedCase = slug ? cases.find((c: Case) => c.public_id === slug) : undefined;
  const selectedCaseId = selectedCase ? slug : null;

  const caseBase = selectedCaseId ? `/cases/${selectedCaseId}` : '';
  const sectionPath = selectedCaseId && location.pathname.startsWith(caseBase)
    ? location.pathname.slice(caseBase.length).replace(/^\/+/, '')
    : '';
  const activeSectionId = CASE_SECTIONS.find((s) => sectionPath.startsWith(s.id))?.id;
  const isSettingsActive = !!selectedCaseId && sectionPath.startsWith('settings');

  const settingsHref = selectedCaseId ? `${caseBase}/settings` : '#';

  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c: Case) =>
      c.name.toLowerCase().includes(q) ||
      (c.client_name ?? '').toLowerCase().includes(q) ||
      (c.opposing_party_name ?? '').toLowerCase().includes(q)
    );
  }, [cases, search]);

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (casePublicId: string) => {
    guardedNavigate(() => navigate(`/cases/${casePublicId}`));
    setOpen(false);
  };

  const sectionLinkCls = (isActive: boolean) =>
    `flex items-center gap-2 px-3 py-2 rounded-md hover:bg-chrome-hover transition-colors text-sm ${
      isActive ? 'bg-chrome-active-bg text-chrome-active-text font-medium' : ''
    }`;

  const collapsedLinkCls = (isActive: boolean) =>
    `p-2 rounded-md hover:bg-chrome-hover transition-colors ${
      isActive ? 'bg-chrome-active-bg text-chrome-active-text' : ''
    }`;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <aside
        data-surface="chrome"
        className={`${sidebarOpen ? 'w-64' : 'w-11'} bg-chrome-bg text-chrome-text flex flex-col flex-shrink-0 border-r border-chrome-sep`}
      >
        {/* Header */}
        {sidebarOpen ? (
          <div className="h-16 flex-shrink-0 px-4 flex items-center justify-between gap-2 border-b border-chrome-sep">
            <Link
              to="/"
              className="flex items-center gap-3 min-w-0 hover:text-chrome-logo-hover transition-colors"
              title="Home"
            >
              <Scale size={24} className="flex-shrink-0" />
              <span className="text-lg font-semibold truncate">Shale</span>
            </Link>
            <IconButton
              icon={PanelLeftClose}
              aria-label="Collapse sidebar"
              variant="ghost"
              onClick={() => setSidebarOpen(false)}
              className="flex-shrink-0 text-chrome-text hover:bg-chrome-hover hover:text-chrome-text"
            />
          </div>
        ) : (
          <div className="h-16 flex-shrink-0 px-4 flex items-center justify-center border-b border-chrome-sep">
            <div className="relative group">
              <Link
                to="/"
                className="flex items-center justify-center p-1 rounded hover:bg-chrome-hover transition-colors group-hover:opacity-0"
                title="Home"
                aria-label="Home"
              >
                <Scale size={24} />
              </Link>
              <IconButton
                icon={PanelLeftOpen}
                aria-label="Open sidebar"
                variant="ghost"
                onClick={() => setSidebarOpen(true)}
                className="absolute inset-0 h-full w-full opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto text-chrome-text hover:bg-chrome-hover hover:text-chrome-text"
              />
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col p-3 gap-2 overflow-y-auto">
          {sidebarOpen && (
            <>
              {/* Searchable case selector */}
              <div ref={dropdownRef} className="relative">
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => setOpen(v => !v)}
                  className="justify-between px-3 font-normal bg-chrome-selector-bg border-chrome-selector-border text-chrome-text hover:bg-chrome-selector-hover hover:text-chrome-text"
                >
                  <span className="min-w-0 flex-1 truncate text-left">
                    {selectedCase ? selectedCase.name : 'Select Case'}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`flex-shrink-0 text-chrome-muted transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </Button>

                {open && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-surface-card rounded-2xl shadow-lg border border-border z-50 overflow-hidden animate-pop">
                    {/* Search */}
                    <div className="p-2 border-b border-border-subtle">
                      <Input
                        ref={inputRef}
                        type="text"
                        size="sm"
                        leading={<Search size={16} />}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search cases..."
                      />
                    </div>

                    {/* List */}
                    <div className="max-h-56 overflow-y-auto py-1">
                      {filteredCases.length === 0 ? (
                        <p className="text-center text-ink-faint text-xs py-4">
                          {search ? 'No cases match' : 'No cases yet'}
                        </p>
                      ) : (
                        filteredCases.map((c: Case) => (
                          <Button
                            key={c.id}
                            variant="ghost"
                            fullWidth
                            onClick={() => handleSelect(c.public_id)}
                            className={`h-auto flex-col items-start gap-0 rounded-none border-0 px-3 py-2 font-normal ${
                              c.public_id === slug
                                ? 'bg-green-100 text-green-900 hover:bg-green-100'
                                : 'text-ink-soft hover:bg-surface-page'
                            }`}
                          >
                            <span className="w-full text-sm font-medium truncate text-left">{c.name}</span>
                            {c.client_name && (
                              <span className="w-full text-xs text-ink-faint truncate text-left">{c.client_name}</span>
                            )}
                          </Button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                fullWidth
                icon={Plus}
                onClick={() => { guardedNavigate(() => navigate('/cases/new')); setOpen(false); }}
                className="h-9 justify-start rounded-md px-3 text-sm font-normal text-chrome-text hover:bg-chrome-hover hover:text-chrome-text"
              >
                New Case
              </Button>

              {/* Case sections — only when a case is open */}
              {selectedCaseId && (
                <>
                  <div className="t-eyebrow mt-2 mb-1 px-3 text-chrome-muted">
                    Case
                  </div>
                  {CASE_SECTIONS.map((section) => {
                    const Icon = section.icon;
                    const isActive = section.id === activeSectionId && !isSettingsActive;
                    return (
                      <Link
                        key={section.id}
                        to={`${caseBase}/${section.defaultPath}`}
                        className={sectionLinkCls(isActive)}
                      >
                        <Icon size={16} />
                        {section.label}
                      </Link>
                    );
                  })}
                  <Link
                    to={settingsHref}
                    className={sectionLinkCls(isSettingsActive)}
                  >
                    <ClipboardList size={16} />
                    Case Info
                  </Link>
                  <div className="mt-2 border-t border-chrome-sep" />
                </>
              )}

              <Link
                to="/"
                className={sectionLinkCls(location.pathname === '/')}
              >
                <FolderOpen size={16} />
                All Cases
              </Link>

              {isAdmin && (
                <>
                  <Link
                    to="/admin/audit-log"
                    className={sectionLinkCls(location.pathname.startsWith('/admin/audit-log'))}
                  >
                    <Shield size={16} />
                    Audit Log
                  </Link>
                  <Link
                    to="/admin/extractor-benchmark"
                    className={sectionLinkCls(location.pathname.startsWith('/admin/extractor-benchmark'))}
                  >
                    <FlaskConical size={16} />
                    Extractor Benchmark
                  </Link>
                </>
              )}
            </>
          )}

          {!sidebarOpen && (
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="ghost"
                aria-label="New Case"
                title="New Case"
                onClick={() => guardedNavigate(() => navigate('/cases/new'))}
                className="h-9 w-9 p-0 rounded-md text-chrome-text hover:bg-chrome-hover hover:text-chrome-text"
              >
                <Plus size={20} aria-hidden="true" />
              </Button>

              {selectedCaseId && (
                <>
                  <div className="w-6 border-t border-chrome-sep my-1" />
                  {CASE_SECTIONS.map((section) => {
                    const Icon = section.icon;
                    const isActive = section.id === activeSectionId && !isSettingsActive;
                    return (
                      <Link
                        key={section.id}
                        to={`${caseBase}/${section.defaultPath}`}
                        className={collapsedLinkCls(isActive)}
                        title={section.label}
                      >
                        <Icon size={20} />
                      </Link>
                    );
                  })}
                  <Link
                    to={settingsHref}
                    className={collapsedLinkCls(isSettingsActive)}
                    title="Case Info"
                  >
                    <ClipboardList size={20} />
                  </Link>
                  <div className="w-6 border-t border-chrome-sep my-1" />
                </>
              )}

              <Link
                to="/"
                className={collapsedLinkCls(location.pathname === '/')}
                title="All Cases"
              >
                <FolderOpen size={20} />
              </Link>
              {isAdmin && (
                <Link
                  to="/admin/audit-log"
                  className={collapsedLinkCls(location.pathname.startsWith('/admin/audit-log'))}
                  title="Audit Log"
                >
                  <Shield size={20} />
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-chrome-sep p-2 flex flex-col gap-1">
          {sidebarOpen ? (
            <Link
              to="/firm-settings"
              className={sectionLinkCls(location.pathname === '/firm-settings')}
            >
              <Settings size={16} />
              Firm Settings
            </Link>
          ) : (
            <Link
              to="/firm-settings"
              className={collapsedLinkCls(location.pathname === '/firm-settings')}
              title="Firm Settings"
            >
              <Settings size={20} />
            </Link>
          )}
          <div className={`flex items-center gap-3 ${sidebarOpen ? 'px-1 pt-1' : 'justify-center pt-1'}`}>
            {CLERK_ENABLED && (
              <UserButton appearance={{ elements: { avatarBox: 'w-8 h-8' } }} />
            )}
            {sidebarOpen && (
              <p className="text-xs text-chrome-muted">Family Law Financials</p>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
